import type { OrbitLayer } from "./types";

export type BranchRole = "how" | "where" | "next" | "kill";

export type Forecast = {
  id: string;
  claim: string;
  year: number;
  test: string;
  disprove: string;
  layer: OrbitLayer;
  tags: string[];
  children: { id: string; role: BranchRole }[];
  method: {
    watched: string[];
    mix: string;
  };
};

export const ROLE_PLAIN: Record<BranchRole, string> = {
  how: "How it happens",
  where: "Where it spreads",
  next: "What follows",
  kill: "What would kill it",
};

export const FORECASTS: Forecast[] = [
  {
    id: "logdrum-afterhours",
    claim: "After 2am, US rooms lock 112–118 BPM log-drum as the night default",
    year: 2027,
    test: "BPM and kick pattern in recorded US club sets after 02:00.",
    disprove: "Post-2am rooms stay 124–130 four-on-the-floor with no log-drum.",
    layer: "inner",
    tags: ["club", "amapiano", "pulse"],
    children: [
      { id: "joburg-to-us-bounce", role: "how" },
      { id: "detroit-kick-export", role: "how" },
      { id: "baile-sub-export", role: "where" },
      { id: "club-exclusive-lowend", role: "next" },
      { id: "four-on-floor-holds", role: "kill" },
    ],
    method: {
      watched: ["late-night club tempo", "kick/log-drum shape", "which cities copy the night"],
      mix: "DJ pickup + city spread + speed of change",
    },
  },
  {
    id: "joburg-to-us-bounce",
    claim: "Joburg taxi-log programming becomes the default US pop bounce by 2028",
    year: 2028,
    test: "Log-drum + delayed kick in US rhythmic / pop productions.",
    disprove: "US pop bounce stays 808-trap or four-on-the-floor house.",
    layer: "inner",
    tags: ["johannesburg", "pop"],
    children: [
      { id: "lagos-pack-booth", role: "how" },
      { id: "chant-808-hybrid", role: "next" },
      { id: "four-on-floor-holds", role: "kill" },
    ],
    method: {
      watched: ["production drum programming", "pop/rhythmic releases", "booth sample packs"],
      mix: "city spread + sounds mixing + sharing",
    },
  },
  {
    id: "detroit-kick-export",
    claim: "Detroit closed-room kick patterns land in São Paulo baile production by 2027",
    year: 2027,
    test: "Kick envelopes in baile tracks matching Detroit techno kick families.",
    disprove: "Baile low-end stays local Brazilian patterns only.",
    layer: "inner",
    tags: ["detroit", "baile"],
    children: [
      { id: "baile-sub-export", role: "next" },
      { id: "mpls-house-undercount", role: "where" },
    ],
    method: {
      watched: ["kick shape across cities", "rooms the apps do not map"],
      mix: "city spread + newness + DJ pickup",
    },
  },
  {
    id: "baile-sub-export",
    claim: "São Paulo baile 30–60Hz sub tuning shows up in European club masters",
    year: 2028,
    test: "Sub energy 30–60Hz in EU club masters vs 2024 club-cut baselines.",
    disprove: "EU club cuts keep a 50–80Hz house/techno sub.",
    layer: "middle",
    tags: ["sao paulo", "sub"],
    children: [
      { id: "club-exclusive-lowend", role: "next" },
      { id: "phone-collapse-mix", role: "next" },
    ],
    method: {
      watched: ["club-cut low end", "EU vs BR master differences"],
      mix: "sounds mixing + DJ pickup + new tools",
    },
  },
  {
    id: "club-exclusive-lowend",
    claim: "30–80Hz is mixed as the club language and withheld from phone masters",
    year: 2029,
    test: "Same title: club master vs streaming master energy in 30–80Hz.",
    disprove: "Phone masters carry the same 30–80Hz as the club cut.",
    layer: "outer",
    tags: ["club", "format"],
    children: [{ id: "phone-collapse-mix", role: "next" }],
    method: {
      watched: ["two-master workflows", "phone vs room playback"],
      mix: "new tools + DJ pickup + speeding up",
    },
  },
  {
    id: "four-on-floor-holds",
    claim: "US after-hours stay 124–130 four-on-the-floor through 2028 — log-drum never locks",
    year: 2028,
    test: "Post-2am US set BPM remaining 124–130 with no log-drum.",
    disprove: "112–118 log-drum becomes the measured after-hours default.",
    layer: "outer",
    tags: ["counter", "house"],
    children: [],
    method: {
      watched: ["the same late-night tapes", "failure of the log-drum claim"],
      mix: "counter-signal weight against the parent",
    },
  },
  {
    id: "catalog-hook-layer",
    claim: "Unlabeled 90s vocal architecture returns as the hook layer on trap-low pop",
    year: 2028,
    test: "Hook phrases: 90s chant/repetition metrics on tracks with 808 sub.",
    disprove: "Trap-low pop keeps rap-verse hooks, no chant-pop vocal grid.",
    layer: "inner",
    tags: ["archive", "vocal"],
    children: [
      { id: "chant-808-hybrid", role: "next" },
      { id: "reissue-as-stems", role: "how" },
      { id: "skipgate-aandr", role: "where" },
      { id: "rap-hook-holds", role: "kill" },
    ],
    method: {
      watched: ["hook repetition", "sub under the hook", "catalog reuse"],
      mix: "sounds mixing + newness + speed",
    },
  },
  {
    id: "chant-808-hybrid",
    claim: "1990s chant-pop hooks over 808 sub become the 2027 US radio hybrid",
    year: 2027,
    test: "Top 40 / rhythmic radio: vocal syllable density + sub energy vs 2024.",
    disprove: "Radio stays guitar-led or pure trap with no chant architecture.",
    layer: "middle",
    tags: ["radio", "hybrid"],
    children: [
      { id: "skipgate-aandr", role: "where" },
      { id: "rap-hook-holds", role: "kill" },
    ],
    method: {
      watched: ["radio hook shape", "sub under vocals"],
      mix: "sounds mixing + sharing + speed",
    },
  },
  {
    id: "reissue-as-stems",
    claim: "Catalog reissues ship as stem kits for booth use, not only 12-inch and WAV",
    year: 2028,
    test: "Major catalog campaigns include stem kits in the SKU list.",
    disprove: "Reissues stay stereo-only.",
    layer: "middle",
    tags: ["archive", "stems"],
    children: [
      { id: "vinyl-short-run", role: "next" },
      { id: "stem-license-freeze", role: "kill" },
    ],
    method: {
      watched: ["catalog SKUs", "booth file types"],
      mix: "new tools + sharing",
    },
  },
  {
    id: "rap-hook-holds",
    claim: "Trap-low pop keeps rap-verse hooks through 2028 — no chant-pop grid returns",
    year: 2028,
    test: "Hook metrics stay verse/rap, not chant-pop repetition.",
    disprove: "Chant-pop architecture is measured on trap-low hits.",
    layer: "outer",
    tags: ["counter"],
    children: [],
    method: {
      watched: ["the same hook metrics as the parent", "failure of the chant claim"],
      mix: "counter-signal weight against the parent",
    },
  },
  {
    id: "phone-collapse-mix",
    claim: "By 2031, club mixes are authored in stems, then crushed to stereo for phones",
    year: 2031,
    test: "Deliverables list stem masters + a phone-checked stereo fold.",
    disprove: "Spatial phone playback wins and the stereo fold is dropped.",
    layer: "outer",
    tags: ["horizon", "stems"],
    children: [
      { id: "stem-default-berlin", role: "how" },
      { id: "club-exclusive-lowend", role: "next" },
      { id: "stem-license-freeze", role: "kill" },
      { id: "human-headline-split", role: "next" },
    ],
    method: {
      watched: ["delivery formats", "booth vs phone masters"],
      mix: "new tools + DJ pickup + speeding up",
    },
  },
  {
    id: "stem-default-berlin",
    claim: "By 2028, Berlin peak-hour booths mix in live stems by default",
    year: 2028,
    test: "Share of 200+ cap rooms running stem-split decks Saturday 1–4am.",
    disprove: "Headliners go back to two-channel vinyl/USB.",
    layer: "inner",
    tags: ["berlin", "stems"],
    children: [
      { id: "human-headline-split", role: "next" },
      { id: "stem-license-freeze", role: "kill" },
    ],
    method: {
      watched: ["booth software in peak rooms", "set recordings"],
      mix: "DJ pickup + new tools + speed",
    },
  },
  {
    id: "stem-license-freeze",
    claim: "A US mechanical fight freezes legal stem use in clubs through 2027",
    year: 2027,
    test: "US venues/DJs pulling stem software after a published rights action.",
    disprove: "A blanket stem license ships and booths keep stems.",
    layer: "outer",
    tags: ["rights"],
    children: [{ id: "umg-ai-rider", role: "next" }],
    method: {
      watched: ["rights actions", "venue software policy"],
      mix: "new tools + counter-pressure",
    },
  },
  {
    id: "human-headline-split",
    claim: "Warm-up slots at 200-cap rooms go generated; headliners stay human through 2029",
    year: 2029,
    test: "Billing: opening-slot tool credits vs headliner human identity.",
    disprove: "Headliners billed as generated, or warm-ups stay fully human.",
    layer: "outer",
    tags: ["ai", "dj"],
    children: [],
    method: {
      watched: ["who is billed", "which slot is generated"],
      mix: "new tools + DJ pickup + counter-signal",
    },
  },
  {
    id: "umg-ai-rider",
    claim: "Majors attach an AI-training rider; independents get none through 2027",
    year: 2027,
    test: "Published major contracts vs independent DSP statements.",
    disprove: "No rider appears, or independents get the same terms.",
    layer: "outer",
    tags: ["rights", "ai"],
    children: [],
    method: {
      watched: ["contract language", "independent vs major terms"],
      mix: "money + new tools",
    },
  },
  {
    id: "lagos-pack-booth",
    claim: "Lagos percussion packs outpace label promos as the booth source in UK rooms",
    year: 2027,
    test: "Pack credits in UK DJ sets vs official promo pools.",
    disprove: "UK booths stay on official promo pools only.",
    layer: "middle",
    tags: ["lagos", "commons"],
    children: [
      { id: "whatsapp-aar", role: "how" },
      { id: "joburg-to-us-bounce", role: "next" },
      { id: "promo-pool-holds", role: "kill" },
    ],
    method: {
      watched: ["what files DJs load", "promo vs informal packs"],
      mix: "sharing + city spread + DJ pickup",
    },
  },
  {
    id: "whatsapp-aar",
    claim: "Uncredited night recordings become A&R input before any DSP page exists",
    year: 2027,
    test: "Time from first club/phone clip to official DSP release.",
    disprove: "Hits still originate on official DSP pages and editorial lists.",
    layer: "inner",
    tags: ["liminal", "aar"],
    children: [
      { id: "seoul-chop-leak", role: "where" },
      { id: "london-pirate-channel", role: "where" },
      { id: "dsp-first-holds", role: "kill" },
    ],
    method: {
      watched: ["first appearance of a sound", "DSP page date vs room date"],
      mix: "speed + sharing + city spread",
    },
  },
  {
    id: "seoul-chop-leak",
    claim: "Seoul after-hours vocal chops enter global K-export with no DSP genre tag",
    year: 2027,
    test: "Export singles using after-hours chops while metadata stays 'k-pop'.",
    disprove: "K-export vocals stay studio-clean and tagged.",
    layer: "inner",
    tags: ["seoul"],
    children: [{ id: "chant-808-hybrid", role: "next" }],
    method: {
      watched: ["vocal chop DNA", "export metadata"],
      mix: "sounds mixing + city spread",
    },
  },
  {
    id: "london-pirate-channel",
    claim: "London community/pirate leftovers remain the scout layer DSPs cannot score",
    year: 2028,
    test: "First-play venue for UK breakthroughs: community vs DSP.",
    disprove: "UK breakthroughs start on DSP editorial.",
    layer: "middle",
    tags: ["london"],
    children: [{ id: "skipgate-aandr", role: "next" }],
    method: {
      watched: ["first play location", "DSP scene maps"],
      mix: "city spread + counter-signal",
    },
  },
  {
    id: "skipgate-aandr",
    claim: "First-7-second skip rate becomes the A&R gate, replacing radio adds",
    year: 2027,
    test: "Label decks: skip-7s as a kill metric vs radio add reports.",
    disprove: "Radio adds and human playlist picks still decide budgets.",
    layer: "middle",
    tags: ["algorithm"],
    children: [
      { id: "playlist-share-drop", role: "next" },
      { id: "discovery-mode-tax", role: "how" },
    ],
    method: {
      watched: ["how labels kill a record", "radio vs skip reports"],
      mix: "speed + sharing + new tools",
    },
  },
  {
    id: "playlist-share-drop",
    claim: "Official editorial playlists lose share to auto-mix after three listener loops",
    year: 2028,
    test: "Editorial vs autoplay share for new tracks after week 3.",
    disprove: "Editorial lists keep the majority of new-track discovery past week 3.",
    layer: "outer",
    tags: ["playlist"],
    children: [],
    method: {
      watched: ["where a new track is heard after week 3"],
      mix: "sharing + new tools + speeding up",
    },
  },
  {
    id: "discovery-mode-tax",
    claim: "Reduced-royalty discovery programs take 25%+ of new-artist streams by 2027",
    year: 2027,
    test: "Reported new-artist stream share under reduced-royalty programs.",
    disprove: "Platforms drop reduced-royalty discovery.",
    layer: "middle",
    tags: ["money"],
    children: [{ id: "umg-ai-rider", role: "next" }],
    method: {
      watched: ["payout programs", "new-artist stream share"],
      mix: "money + new tools",
    },
  },
  {
    id: "vinyl-short-run",
    claim: "Default 12-inch run drops under 800 copies; 180g becomes a $40 object",
    year: 2028,
    test: "Median independent 12-inch run length and street price.",
    disprove: "Independent 12-inch runs stay 1500+ at sub-$25 street.",
    layer: "outer",
    tags: ["vinyl"],
    children: [],
    method: {
      watched: ["pressing run length", "street price"],
      mix: "money + speeding up",
    },
  },
  {
    id: "mpls-house-undercount",
    claim: "Midwest house rooms keep exporting DJs while platform maps undercount them",
    year: 2028,
    test: "DJ origin cities vs platform 'scene' maps for house.",
    disprove: "Platform maps list Midwest house at true activity.",
    layer: "middle",
    tags: ["minneapolis"],
    children: [],
    method: {
      watched: ["DJ origin vs platform maps"],
      mix: "city spread + counter-signal",
    },
  },
  {
    id: "promo-pool-holds",
    claim: "UK booths stay on official promo pools through 2028 — informal packs never win",
    year: 2028,
    test: "UK set credits remain promo-pool only.",
    disprove: "Lagos/Joburg packs are the measured booth source.",
    layer: "outer",
    tags: ["counter"],
    children: [],
    method: {
      watched: ["the same booth file sources", "failure of the pack claim"],
      mix: "counter-signal weight against the parent",
    },
  },
  {
    id: "dsp-first-holds",
    claim: "Hits still start on official DSP pages through 2028 — room clips do not beat the store",
    year: 2028,
    test: "Official DSP date still precedes first room/phone clip.",
    disprove: "Room/phone clip consistently precedes the DSP page.",
    layer: "outer",
    tags: ["counter"],
    children: [],
    method: {
      watched: ["first-seen date: room vs store"],
      mix: "counter-signal weight against the parent",
    },
  },
];

const BY_ID = new Map(FORECASTS.map((f) => [f.id, f]));

export function forecastById(id?: string | null) {
  if (!id) return undefined;
  return BY_ID.get(id);
}

export function forecastByClaim(claim: string) {
  const c = claim.trim().toLowerCase();
  return FORECASTS.find((f) => f.claim.toLowerCase() === c || f.id === claim);
}

const QUERY_ROOT: { test: RegExp; id: string }[] = [
  { test: /archive|lineage|catalog|90s|reissue|vinyl|the archive/, id: "catalog-hook-layer" },
  { test: /pulse|lived|now|club|2am|after hours|amapiano|log|the pulse/, id: "logdrum-afterhours" },
  { test: /horizon|25 years|2030|2031|immersive|spatial|future|the horizon/, id: "phone-collapse-mix" },
  { test: /commons|collaborative|shared|lagos|pack|the commons/, id: "lagos-pack-booth" },
  { test: /liminal|emerging|unresolved|whatsapp|aar|the liminal/, id: "whatsapp-aar" },
  { test: /stem|berlin|booth/, id: "stem-default-berlin" },
  { test: /own|rights|ai rider|copyright/, id: "umg-ai-rider" },
  { test: /skip|algorithm|playlist|chart|stream/, id: "skipgate-aandr" },
  { test: /detroit/, id: "detroit-kick-export" },
  { test: /s[aã]o paulo|baile/, id: "baile-sub-export" },
  { test: /johannesburg|joburg/, id: "joburg-to-us-bounce" },
  { test: /seoul/, id: "seoul-chop-leak" },
  { test: /london|pirate/, id: "london-pirate-channel" },
  { test: /ai dj|generated/, id: "human-headline-split" },
];

export function rootForecast(topic: string): Forecast {
  const t = topic.toLowerCase();
  const exact = forecastByClaim(topic);
  if (exact) return exact;
  for (const row of QUERY_ROOT) {
    if (row.test.test(t)) return BY_ID.get(row.id)!;
  }
  return BY_ID.get("logdrum-afterhours")!;
}

export function childForecasts(parent: Forecast, visited: Set<string>): { forecast: Forecast; role: BranchRole }[] {
  const out: { forecast: Forecast; role: BranchRole }[] = [];
  const seen = new Set<string>([parent.id, ...visited]);
  for (const edge of parent.children) {
    if (seen.has(edge.id)) continue;
    const f = BY_ID.get(edge.id);
    if (!f) continue;
    seen.add(f.id);
    out.push({ forecast: f, role: edge.role });
  }
  return out;
}
